import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { loadStaticData } from '@/lib/offlineData';

export interface StateScenarioCategoryStat {
  state: string;
  category: string;
  hospital_count: number;
  scenario_count: number;
  median_markup_pct: number;
  avg_markup_pct: number;
  avg_hospital_price: number;
  avg_national_price: number;
  avg_coverage_pct: number;
  min_markup_pct: number;
  max_markup_pct: number;
}

export interface StateScenarioOverallStat {
  state: string;
  hospital_count: number;
  scenario_count: number;
  median_markup_pct: number;
  avg_markup_pct: number;
  avg_hospital_price: number;
  avg_national_price: number;
  avg_coverage_pct: number;
  min_markup_pct: number;
  max_markup_pct: number;
}

interface StaticScenarioStatsData {
  generated_at: string;
  categoryStats: StateScenarioCategoryStat[];
  overallStats: StateScenarioOverallStat[];
}

export function useStateScenarioStats() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['state-scenario-stats-v2'],
    queryFn: async (): Promise<{ categoryStats: StateScenarioCategoryStat[]; overallStats: StateScenarioOverallStat[] }> => {
      // Try static data first (encoded .dat on Capacitor, .json on web)
      try {
        const staticData = await loadStaticData<StaticScenarioStatsData>('scenario_stats.json');
        if (staticData && staticData.categoryStats?.length > 0) {
          console.log(`[ScenarioStats] Using static data (${staticData.categoryStats.length} category, ${staticData.overallStats.length} overall)`);
          return {
            categoryStats: staticData.categoryStats,
            overallStats: staticData.overallStats,
          };
        }
      } catch (e) {
        console.warn('[ScenarioStats] Static data error, falling back to API:', e);
      }

      // Supabase fallback
      const [catRes, overallRes] = await Promise.all([
        supabase.from('state_scenario_category_stats' as any).select('*'),
        supabase.from('state_scenario_overall_stats' as any).select('*'),
      ]);

      if (catRes.error) throw catRes.error;
      if (overallRes.error) throw overallRes.error;

      return {
        categoryStats: (catRes.data || []) as StateScenarioCategoryStat[],
        overallStats: (overallRes.data || []) as StateScenarioOverallStat[],
      };
    },
    staleTime: 1000 * 60 * 60,
  });

  // Get unique categories from the data
  const categories = data?.categoryStats
    ? [...new Set(data.categoryStats.map(r => r.category))].sort()
    : [];

  return {
    categoryStats: data?.categoryStats || [],
    overallStats: data?.overallStats || [],
    categories,
    isLoading,
    error,
  };
}
